import { type PromoFeatureWithAccess } from '@luxuryescapes/contract-svc-promo';

export type PromoFeatureAccessBadge = {
  label: string;
  description: string;
  kind: 'optIn' | 'role';
};

function roleLabel(key: string) {
  return key.replace(/[-_]/g, ' ').replace(/^promo /, 'Promo ');
}

/** Describe granted access, including parent requirements, without treating an OR branch as mandatory. */
export function getPromoFeatureAccessBadges(feature: PromoFeatureWithAccess): Array<PromoFeatureAccessBadge> {
  if (feature.canUse !== true) {
    return [];
  }

  const badges = new Map<string, PromoFeatureAccessBadge>();
  for (const reason of feature.accessReasons) {
    if (reason.result !== true) {
      continue;
    }
    const requirement = reason.requirement;
    const inherited = reason.featureKey !== feature.key ? ' through its parent feature' : '';
    const alternatives = feature.accessReasons.filter(
      (other) =>
        other.featureKey === reason.featureKey &&
        other.operator === 'or' &&
        (other.requirement.type === 'auth_role' ||
          other.requirement.type === 'promo_role' ||
          other.requirement.type === 'optIn') &&
        other !== reason,
    );
    const alternativeGranted = reason.operator === 'or' && alternatives.some((other) => other.result === true);

    if (requirement.type === 'optIn') {
      badges.set(`optIn:${requirement.key}`, {
        label: 'Session opt-in',
        description: alternativeGranted
          ? `You opted into ${feature.displayName} for this browser session${inherited}. Another access path also grants access.`
          : `${feature.displayName} is available because you opted in for this browser session${inherited}.`,
        kind: 'optIn',
      });
    } else if (requirement.type === 'auth_role' || requirement.type === 'promo_role') {
      const label = roleLabel(requirement.key);
      badges.set(`${requirement.type}:${requirement.key}`, {
        label: `${label} access`,
        description:
          reason.operator === 'or' && alternatives.length > 0
            ? `${label} is one of the roles or opt-ins that grants access to ${feature.displayName}${inherited}.`
            : `${feature.displayName} requires ${label} access${inherited}.`,
        kind: 'role',
      });
    }
  }
  return [...badges.values()];
}
